import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import { apiService } from '../services/api';
import type { ParserProfile, CreateParserProfilePayload } from '../types/timekeeping';

const FILE_TYPES = ['csv', 'txt', 'excel', 'xls', 'access_mdb', 'access_accdb'];

const FILE_TYPE_LABELS: Record<string, string> = {
  csv: 'CSV',
  txt: 'TXT',
  excel: 'Excel (xlsx)',
  xls: 'Excel (xls)',
  access_mdb: 'Access (mdb)',
  access_accdb: 'Access (accdb)',
};
const WORKER_TYPES = ['lambda', 'remote'];
const LAMBDA_MEMORIES = [128, 256, 512, 1024, 1536, 3008];

const emptyForm = (): CreateParserProfilePayload => ({
  name: '',
  fileType: 'csv',
  workerType: 'lambda',
  config: '{}',
  workerConfig: '{"memory":512,"timeout":300}',
});

const ParserProfiles: React.FC = () => {
  const [profiles, setProfiles] = useState<ParserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  // Derived worker config fields
  const [lambdaMemory, setLambdaMemory] = useState(512);
  const [lambdaTimeout, setLambdaTimeout] = useState(300);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.listParserProfiles();
      setProfiles(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const syncWorkerConfig = (wt: string, mem: number, timeout: number) => {
    if (wt === 'lambda') {
      setForm(f => ({ ...f, workerConfig: JSON.stringify({ memory: mem, timeout }) }));
    } else {
      setForm(f => ({ ...f, workerConfig: '{}' }));
    }
  };

  const openCreate = () => {
    setEditingId(null);
    const f = emptyForm();
    setForm(f);
    setLambdaMemory(512);
    setLambdaTimeout(300);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (p: ParserProfile) => {
    setEditingId(p.id);
    let parsedWC: any = {};
    const wcString = typeof p.workerConfig === 'string' ? p.workerConfig : JSON.stringify(p.workerConfig);
    const cfgString = typeof p.config === 'string' ? p.config : JSON.stringify(p.config);
    try { parsedWC = JSON.parse(wcString); } catch { /* ignore */ }
    setForm({ name: p.name, fileType: p.fileType, workerType: p.workerType, config: cfgString, workerConfig: wcString });
    if (p.workerType === 'lambda') {
      setLambdaMemory(parsedWC.memory ?? 512);
      setLambdaTimeout(parsedWC.timeout ?? 300);
    }
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    // Sync workerConfig before save
    const finalForm = { ...form };
    if (form.workerType === 'lambda') {
      finalForm.workerConfig = JSON.stringify({ memory: lambdaMemory, timeout: lambdaTimeout });
    } else {
      finalForm.workerConfig = '{}';
    }
    try {
      const parsedConfig = JSON.parse(finalForm.config as string);
      const payload = {
        ...finalForm,
        workerConfig: JSON.parse(finalForm.workerConfig as string),
        config: parsedConfig,
      };
      if (editingId) {
        await apiService.updateParserProfile(editingId, payload);
      } else {
        await apiService.createParserProfile(payload);
      }
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await apiService.deleteParserProfile(deleteId);
      setDeleteId(null);
      load();
    } catch (e: any) {
      setError(e.message || 'Delete failed');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <Box p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Parser Profiles</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Profile</Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>File Type</TableCell>
                  <TableCell>Worker Type</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No parser profiles yet.</TableCell>
                  </TableRow>
                ) : profiles.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{FILE_TYPE_LABELS[p.fileType] ?? p.fileType}</TableCell>
                    <TableCell>{p.workerType}</TableCell>
                    <TableCell>{new Date(p.updatedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteId(p.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create / Edit dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ ref: navContainerRef }}
        >
          <FormNavigationProvider containerRef={navContainerRef}>
            <DialogTitle>{editingId ? 'Edit Parser Profile' : 'New Parser Profile'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={1}>
                {saveError && <Alert severity="error">{saveError}</Alert>}

                <NavTextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />

                <FormControl fullWidth>
                  <InputLabel>File Type</InputLabel>
                  <NavSelect value={form.fileType} label="File Type" onChange={e => setForm(f => ({ ...f, fileType: e.target.value as string }))}>
                    {FILE_TYPES.map(t => <MenuItem key={t} value={t}>{FILE_TYPE_LABELS[t] ?? t}</MenuItem>)}
                  </NavSelect>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Worker Type</InputLabel>
                  <NavSelect value={form.workerType} label="Worker Type" onChange={e => {
                    const wt = e.target.value as string;
                    setForm(f => ({ ...f, workerType: wt }));
                    syncWorkerConfig(wt, lambdaMemory, lambdaTimeout);
                  }}>
                    {WORKER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </NavSelect>
                </FormControl>

                {/* Lambda worker config */}
                {form.workerType === 'lambda' && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Memory (MB)</InputLabel>
                      <NavSelect value={lambdaMemory} label="Memory (MB)" onChange={e => {
                        const v = Number(e.target.value);
                        setLambdaMemory(v);
                        syncWorkerConfig('lambda', v, lambdaTimeout);
                      }}>
                        {LAMBDA_MEMORIES.map(m => <MenuItem key={m} value={m}>{m} MB</MenuItem>)}
                      </NavSelect>
                    </FormControl>
                    <NavTextField label="Timeout (seconds, 60–900)" type="number" navFieldType="number" value={lambdaTimeout}
                      onChange={e => {
                        const v = Number(e.target.value);
                        setLambdaTimeout(v);
                        syncWorkerConfig('lambda', lambdaMemory, v);
                      }} inputProps={{ min: 60, max: 900 }} fullWidth />
                  </>
                )}

                <NavTextField
                  label="Config (JSON)"
                  value={form.config}
                  onChange={e => setForm(f => ({ ...f, config: e.target.value }))}
                  multiline rows={4} fullWidth
                  helperText="Type-specific file parsing configuration"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={20} /> : (editingId ? 'Save' : 'Create')}
              </Button>
            </DialogActions>
          </FormNavigationProvider>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
          <DialogTitle>Delete Parser Profile?</DialogTitle>
          <DialogContent>This cannot be undone.</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default ParserProfiles;
